import { SetStateAction, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';

type DataGridSearchParamsOptions<TFilters extends Record<string, string>> = {
	initialPaginationModel: GridPaginationModel;
	initialSortModel: GridSortModel;
	initialFilters: TFilters;
	pageParamName?: string;
	pageSizeParamName?: string;
	sortFieldParamName?: string;
	sortDirectionParamName?: string;
};

type DataGridSearchParamsState<TFilters extends Record<string, string>> = {
	paginationModel: GridPaginationModel;
	setPaginationModel: (value: SetStateAction<GridPaginationModel>) => void;
	sortModel: GridSortModel;
	setSortModel: (value: SetStateAction<GridSortModel>) => void;
	filters: TFilters;
	setFilter: <TKey extends keyof TFilters>(key: TKey, value: TFilters[TKey], resetPage?: boolean) => void;
	setFilters: (values: Partial<TFilters>, resetPage?: boolean) => void;
	resetSearchParams: () => void;
};

function getNumberParam(searchParams: URLSearchParams, paramName: string, defaultValue: number, minimumValue: number): number {
	const value = Number(searchParams.get(paramName));
	return Number.isInteger(value) && value >= minimumValue ? value : defaultValue;
}

function setOrDeleteDefault(params: URLSearchParams, paramName: string, value: string, defaultValue: string) {
	if (value === defaultValue || value === '') {
		params.delete(paramName);
		return;
	}

	params.set(paramName, value);
}

function normalizePaginationModel(model: GridPaginationModel, initialPage: number, initialPageSize: number): GridPaginationModel {
	return {
		page: Number.isInteger(model.page) && model.page >= 0 ? model.page : initialPage,
		pageSize: Number.isInteger(model.pageSize) && model.pageSize >= 1 ? model.pageSize : initialPageSize,
	};
}

export function useDataGridSearchParams<TFilters extends Record<string, string>>({
	initialPaginationModel,
	initialSortModel,
	initialFilters,
	pageParamName = 'pageIndex',
	pageSizeParamName = 'pageSize',
	sortFieldParamName = 'sortBy',
	sortDirectionParamName = 'sortDirection',
}: DataGridSearchParamsOptions<TFilters>): DataGridSearchParamsState<TFilters> {
	const [searchParams, setSearchParams] = useSearchParams();
	const initialPage = initialPaginationModel.page;
	const initialPageSize = initialPaginationModel.pageSize;

	const paginationModel = useMemo<GridPaginationModel>(
		() =>
			normalizePaginationModel(
				{
					page: getNumberParam(searchParams, pageParamName, initialPage, 0),
					pageSize: getNumberParam(searchParams, pageSizeParamName, initialPageSize, 1),
				},
				initialPage,
				initialPageSize,
			),
		[initialPage, initialPageSize, pageParamName, pageSizeParamName, searchParams],
	);

	const sortModel = useMemo<GridSortModel>(() => {
		const sortField = searchParams.get(sortFieldParamName);
		const sortDirection = searchParams.get(sortDirectionParamName);
		if (!sortField || (sortDirection !== 'asc' && sortDirection !== 'desc')) {
			return initialSortModel;
		}

		return [{ field: sortField, sort: sortDirection }];
	}, [initialSortModel, searchParams, sortDirectionParamName, sortFieldParamName]);

	const filters = useMemo<TFilters>(() => {
		const nextFilters = { ...initialFilters };
		for (const key of Object.keys(initialFilters) as (keyof TFilters)[]) {
			nextFilters[key] = (searchParams.get(String(key)) ?? initialFilters[key]) as TFilters[keyof TFilters];
		}

		return nextFilters;
	}, [initialFilters, searchParams]);

	const updateSearchParams = useCallback(
		(update: (params: URLSearchParams) => void) => {
			setSearchParams(
				(current) => {
					const next = new URLSearchParams(current);
					update(next);
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	const setPaginationModel = useCallback(
		(value: SetStateAction<GridPaginationModel>) => {
			const nextModel = normalizePaginationModel(typeof value === 'function' ? value(paginationModel) : value, initialPage, initialPageSize);
			updateSearchParams((params) => {
				setOrDeleteDefault(params, pageParamName, String(nextModel.page), String(initialPage));
				setOrDeleteDefault(params, pageSizeParamName, String(nextModel.pageSize), String(initialPageSize));
			});
		},
		[initialPage, initialPageSize, pageParamName, pageSizeParamName, paginationModel, updateSearchParams],
	);

	const setSortModel = useCallback(
		(value: SetStateAction<GridSortModel>) => {
			const nextModel = typeof value === 'function' ? value(sortModel) : value;
			const sort = nextModel[0];
			updateSearchParams((params) => {
				if (!sort?.field || !sort.sort) {
					params.delete(sortFieldParamName);
					params.delete(sortDirectionParamName);
					return;
				}

				const defaultSort = initialSortModel[0];
				if (sort.field === defaultSort?.field && sort.sort === defaultSort.sort) {
					params.delete(sortFieldParamName);
					params.delete(sortDirectionParamName);
					return;
				}

				params.set(sortFieldParamName, sort.field);
				params.set(sortDirectionParamName, sort.sort);
			});
		},
		[initialSortModel, sortDirectionParamName, sortFieldParamName, sortModel, updateSearchParams],
	);

	const setFilters = useCallback(
		(values: Partial<TFilters>, resetPage: boolean = false) => {
			updateSearchParams((params) => {
				for (const [key, value] of Object.entries(values)) {
					setOrDeleteDefault(params, key, value ?? '', initialFilters[key as keyof TFilters] ?? '');
				}
				if (resetPage) {
					params.delete(pageParamName);
				}
			});
		},
		[initialFilters, pageParamName, updateSearchParams],
	);

	const setFilter = useCallback(
		<TKey extends keyof TFilters>(key: TKey, value: TFilters[TKey], resetPage: boolean = false) => {
			const nextFilters: Partial<TFilters> = {};
			nextFilters[key] = value;
			setFilters(nextFilters, resetPage);
		},
		[setFilters],
	);

	const resetSearchParams = useCallback(() => {
		updateSearchParams((params) => {
			params.delete(pageParamName);
			params.delete(pageSizeParamName);
			params.delete(sortFieldParamName);
			params.delete(sortDirectionParamName);
			for (const key of Object.keys(initialFilters)) {
				params.delete(key);
			}
		});
	}, [initialFilters, pageParamName, pageSizeParamName, sortDirectionParamName, sortFieldParamName, updateSearchParams]);

	return {
		paginationModel,
		setPaginationModel,
		sortModel,
		setSortModel,
		filters,
		setFilter,
		setFilters,
		resetSearchParams,
	};
}
